/**
 * WB4 Depth — action safety policy primitives (no rule-table imports).
 */

import type { WorkbenchActionSurface } from './action-types';

export type WorkbenchRiskTier = 'low' | 'medium' | 'high' | 'finance';

export type RecoverabilityMode =
  | 'none'
  | 'toast_undo'
  | 'revision_restore'
  | 'approval_correction'
  | 'compensating_transaction';

export type WorkbenchActionConfirmation =
  | 'none'
  | 'confirm'
  | 'confirm_with_reason'
  | 'approval_required';

export type WorkbenchActionSafetySurface =
  | 'button'
  | 'context_menu'
  | 'inspector'
  | 'palette'
  | 'bulk'
  | 'ai'
  | 'shortcut';

export interface WorkbenchActionSafety {
  riskTier: WorkbenchRiskTier;
  recoverabilityMode: RecoverabilityMode;
  confirmation: WorkbenchActionConfirmation;
  auditRequired: boolean;
  allowedSurfaces: readonly WorkbenchActionSafetySurface[];
}

const RISK_TIER_DEFAULTS: Record<
  WorkbenchRiskTier,
  Omit<WorkbenchActionSafety, 'allowedSurfaces'>
> = {
  low: {
    riskTier: 'low',
    recoverabilityMode: 'none',
    confirmation: 'none',
    auditRequired: true,
  },
  medium: {
    riskTier: 'medium',
    recoverabilityMode: 'revision_restore',
    confirmation: 'none',
    auditRequired: true,
  },
  high: {
    riskTier: 'high',
    recoverabilityMode: 'approval_correction',
    confirmation: 'confirm',
    auditRequired: true,
  },
  finance: {
    riskTier: 'finance',
    recoverabilityMode: 'compensating_transaction',
    confirmation: 'confirm_with_reason',
    auditRequired: true,
  },
};

export function mapWorkbenchSurfacesToSafetySurfaces(
  surfaces: readonly WorkbenchActionSurface[]
): WorkbenchActionSafetySurface[] {
  const mapped = new Set<WorkbenchActionSafetySurface>();
  for (const surface of surfaces) {
    switch (surface) {
      case 'table-row':
        mapped.add('context_menu');
        mapped.add('bulk');
        break;
      case 'detail-header':
        mapped.add('button');
        break;
      case 'inspector':
        mapped.add('inspector');
        break;
      case 'command':
        mapped.add('palette');
        break;
      case 'shortcut':
        mapped.add('shortcut');
        break;
      case 'board':
      case 'assignment-board':
      case 'my-tasks':
        mapped.add('button');
        break;
      default:
        mapped.add('button');
    }
  }
  return [...mapped];
}

export function buildActionSafety(
  riskTier: WorkbenchRiskTier,
  surfaces: readonly WorkbenchActionSurface[],
  overrides: Partial<WorkbenchActionSafety> = {}
): WorkbenchActionSafety {
  const defaults = RISK_TIER_DEFAULTS[riskTier];
  return {
    ...defaults,
    allowedSurfaces: mapWorkbenchSurfacesToSafetySurfaces(surfaces),
    ...overrides,
    riskTier,
  };
}

/** Phase B gate — static safety policy invariants for golden-path rule-table actions. */
export function collectPhaseBSafetyViolations(
  safety: WorkbenchActionSafety,
  actionKey: string
): string[] {
  const violations: string[] = [];
  const { riskTier, recoverabilityMode, confirmation } = safety;

  if (riskTier === 'finance') {
    if (recoverabilityMode === 'toast_undo') {
      violations.push(`${actionKey}: finance tier must not use toast_undo recoverability`);
    }
    if (confirmation === 'none') {
      violations.push(`${actionKey}: finance tier requires confirmation gate`);
    }
  }

  if (['medium', 'high', 'finance'].includes(riskTier) && recoverabilityMode === 'toast_undo') {
    violations.push(`${actionKey}: ${riskTier} tier must not allow toast undo`);
  }

  if (riskTier === 'medium' && recoverabilityMode === 'toast_undo') {
    violations.push(
      `${actionKey}: medium tier must use revision_restore (disabled_with_reason path)`
    );
  }

  if (['high', 'finance'].includes(riskTier) && confirmation === 'none') {
    violations.push(`${actionKey}: ${riskTier} tier requires confirmation gate`);
  }

  return violations;
}
